import { styled } from "@mui/material/styles";
import MuiRadio from "@mui/material/Radio";
import type { RadioProps as MuiRadioProps } from "@mui/material/Radio";
import type { CSSProperties, FC } from "react";

const sizeStyles: Record<NonNullable<RadioProps["size"]>, CSSProperties> = {
  medium: { width: 24, height: 24 },
  small: { width: 16, height: 16 },
};

type IconProps = { size?: RadioProps["size"]; isReadonly?: boolean };

const BpIcon = styled("span", { shouldForwardProp: prop => prop !== "isReadonly" })<IconProps>(({
  size = "small",
  theme,
  isReadonly,
}) => {
  const { grey, common, primary } = theme.palette;
  return {
    "borderRadius": "50%",
    ...sizeStyles[size],
    "backgroundColor": common.white,
    "boxShadow": `inset 0 0 0 1px ${grey[700]}, inset 0 -1px 0 ${grey[700]}`,
    ...(isReadonly
      ? {}
      : { "input:disabled ~ &": { boxShadow: `inset 0 0 0 1px ${grey[400]}, inset 0 -1px 0 ${grey[400]}` } }),
    ".Mui-focusVisible &": { outline: `2px auto ${primary.main}`, outlineOffset: 2 },
  };
});

const BpCheckedIcon = styled(BpIcon)<IconProps>(({ size = "small", theme, isReadonly }) => {
  const { primary, common } = theme.palette;
  const bgImageStyle: Record<NonNullable<RadioProps["size"]>, CSSProperties> = {
    medium: { backgroundImage: `radial-gradient(${common.white},${common.white} 26%,transparent 32%)` },
    small: { backgroundImage: `radial-gradient(${common.white},${common.white} 22%,transparent 31%)` },
  };
  return {
    "backgroundColor": primary.main,
    "boxShadow": "none",
    ...(isReadonly ? {} : { "input:disabled ~ &": { backgroundColor: primary[200], boxShadow: "none" } }),
    "&:before": { display: "block", ...sizeStyles[size], ...bgImageStyle[size], content: '""' },
    ".Mui-focusVisible &": { outline: `2px auto ${primary.main}`, outlineOffset: 2 },
  };
});

type RadioProps = Omit<
  MuiRadioProps,
  | "icon"
  | "disableRipple"
  | "checkedIcon"
  | "color"
  | "action"
  | "centerRipple"
  | "disableTouchRipple"
  | "focusRipple"
  | "focusVisibleClassName"
  | "LinkComponent"
  | "TouchRippleProps"
  | "touchRippleRef"
> & { isReadonly?: boolean };
export const Radio: FC<RadioProps> = ({ size = "small", isReadonly, ...rest }) => {
  return (
    <MuiRadio
      sx={{ "&:hover": { bgcolor: "transparent" } }}
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon size={size} isReadonly={isReadonly} />}
      icon={<BpIcon size={size} isReadonly={isReadonly} />}
      {...rest}
    />
  );
};
